import isEqual from 'lodash/isEqual';
import type { ReactElement } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  InstantSearchProps,
  InstantSearchServerState,
} from 'react-instantsearch';
import {
  Index,
  InstantSearch,
  InstantSearchSSRProvider,
  useClearRefinements,
  useInstantSearch,
} from 'react-instantsearch';
import { useIntl } from 'react-intl';

import { isEyewearEnabled } from '@/utils/eyewear';
import { asPathOrEmpty, getPathnameFromAsPath } from '@/utils/urlUtils';

import { Configure, PaginatedConfigure } from '@/components/Configure';
import { Container } from '@/components/Container';
import { ListingFilters } from '@/components/ListingFilters';
import {
  getAllTabTotalHits,
  ProductsResults,
  ResultsTabsWrapper,
  StoresResults,
} from '@/components/Search';
import * as SearchMessages from '@/components/Search/messages';
import type { SearchViewType } from '@/components/Search/types';
import { useAlgoliaUserToken } from '@/hooks/useAlgoliaUserToken';
import { useRouter } from '@/hooks/useRouter';
import {
  applyProductConfigurationFilters,
  buildUiStateFromURL,
  getEyewearFilters,
  getIndexName,
  getWatchFilters,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createInsightsOptions } from '@/integrations/algolia/insightsOptions';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { SearchUiState } from '@/integrations/algolia/types';
import type { ContentfulData } from '@/integrations/contentful';
import type { CMSLayoutDataProps } from '@/layouts/Default';
import { Default } from '@/layouts/Default';
import { HITS_PER_PAGE } from '@/lib/const';
import { getRegionFromLocale } from '@/lib/regions';
import { commonMessages } from '@/messages/common';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { useAlgoliaInsightsEnabled } from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';
import type { ListingType } from '@/states/listingState';
import { listingActions } from '@/states/listingState';
import { NoResultsAllTab } from '@/views/SearchView/NoResultsAllTab';

import { buildSearchPageQueryParamsFromUiState } from './queryParams';
import {
  getSearchScopeAnalyticsTags,
  SEARCH_PAGE_ROOT_ANALYTICS_TAGS,
} from './searchAnalyticsTags';
import { SearchIndexStateApplier } from './SearchIndexStateApplier';

import * as S from './SearchView.styles';

const ResetFilters = () => {
  const { asPath } = useRouter();
  const activeTab = asPathOrEmpty(asPath).split('/')[2] as SearchViewType;
  const prevActiveTabRef = useRef(activeTab);
  const { refine: clearRefinements } = useClearRefinements();

  useEffect(() => {
    if (prevActiveTabRef.current !== activeTab) {
      prevActiveTabRef.current = activeTab;
      clearRefinements();
    }
  }, [activeTab]);

  return null;
};

const AllTabResultsReady = ({
  activeTab,
  onReady,
}: {
  activeTab: SearchViewType;
  onReady: () => void;
}) => {
  const { status } = useInstantSearch();
  const hasSeenLoading = useRef(false);
  const hasFired = useRef(false);

  // Reset refs when activeTab changes
  useEffect(() => {
    hasSeenLoading.current = false;
    hasFired.current = false;
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'all' || hasFired.current) {
      return;
    }

    // Track when search starts loading
    if (status === 'loading' || status === 'stalled') {
      hasSeenLoading.current = true;
    }

    // Fire after transitioning from loading to idle
    if (hasSeenLoading.current && status === 'idle') {
      hasFired.current = true;
      onReady();
      return;
    }

    // Fallback: if status is already 'idle' on mount (e.g., SSR), wait a tick
    // to ensure we're past any initial state settling, then fire
    if (status === 'idle' && !hasSeenLoading.current) {
      const timeoutId = setTimeout(() => {
        if (!hasFired.current && activeTab === 'all') {
          hasFired.current = true;
          onReady();
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [activeTab, onReady, status]);

  return null;
};

const AllTabHitsSummary = ({
  filteredResultsReady,
}: {
  filteredResultsReady: boolean;
}) => {
  const { scopedResults } = useInstantSearch();

  if (!filteredResultsReady) {
    return null;
  }

  const totalCount = getAllTabTotalHits(scopedResults);

  return (
    <S.AllTabSummaryWrapper data-test-id='AllTabHitsSummary'>
      {SearchMessages.TotalResults(totalCount)}
    </S.AllTabSummaryWrapper>
  );
};

export type SearchViewProps = {
  /** SSR-generated Algolia user token for consistent rendering (BEC-20960) */
  algoliaUserToken?: string;
  cmsDefaultLayoutData?: CMSLayoutDataProps | null;
  draftMode: boolean;
  facetDataForTranslation?: InstantSearchServerState | null;
  instantSearchProps: Partial<InstantSearchProps>;
  serverState: InstantSearchServerState;
};

export const SearchView = ({
  algoliaUserToken,
  cmsDefaultLayoutData,
  draftMode,
  facetDataForTranslation,
  instantSearchProps,
  serverState,
}: SearchViewProps) => {
  const { asPath, push, query } = useRouter();
  const locale = useContext(LocaleContext);
  const { formatMessage } = useIntl();
  const {
    isLoading,
    isUserInitialized,
    userToken: clientUserToken,
  } = useAlgoliaUserToken();
  const isAlgoliaInsightsEnabled = useAlgoliaInsightsEnabled();
  // BEC-20960: Use SSR token until user state is fully initialized to prevent
  // duplicate Algolia requests with different tokens (anonymous then authenticated).
  const userToken = isUserInitialized
    ? clientUserToken
    : (algoliaUserToken ?? clientUserToken);
  const insightsOptions = useMemo(
    () => (isAlgoliaInsightsEnabled ? createInsightsOptions(userToken) : false),
    [isAlgoliaInsightsEnabled, userToken],
  );
  const { locale: regionLocale } = useRegionProvider();

  // Track whether we should skip URL syncing on initial state changes
  // This prevents the first onStateChange from clearing filters before widgets are ready
  const isInitialMount = useRef(true);
  const lastSyncedState = useRef<SearchUiState | null>(null);

  // Callback for when SearchIndexStateApplier has applied the initial state
  // and the filtered search has completed
  const handleStateApplied = useCallback(() => {
    isInitialMount.current = false;
    setFilteredResultsReady(true);
  }, []);

  const activeTab = asPathOrEmpty(asPath).split('/')[3] as SearchViewType;

  // Reset isInitialMount when the active tab changes (e.g. "More Results" on the all tab
  // navigates to a specific tab). This must happen during render (not in useEffect) so
  // that the guard is active before onStateChange fires from newly mounted widgets.
  const prevActiveTab = useRef(activeTab);
  if (prevActiveTab.current !== activeTab) {
    prevActiveTab.current = activeTab;
    isInitialMount.current = true;
    lastSyncedState.current = null;
  }
  const indexName = getIndexName('PRODUCT', locale);

  // Get the current locale code for translation (e.g., 'ES', 'DE')
  const currentLocaleCode = regionLocale?.code?.toUpperCase() || 'EN_US';

  // Build UI state from URL on client-side to ensure proper state structure
  // The state is structured so:
  // - Root index (indexName) has only 'query' for the SearchBox widget
  // - Nested index (activeTab like 'straps') has refinements for ListingFilters widgets
  const uiStateFromURL = useMemo(() => {
    const region = getRegionFromLocale(locale);
    const fullState = buildUiStateFromURL(
      asPath,
      indexName,
      indexName,
      activeTab,
      region.locale,
      facetDataForTranslation ?? undefined,
      'EN_US', // URL values are in English, translate to current locale for UI
    );

    // Get the state for the active tab (this has all filters including query)
    const activeTabState = fullState[activeTab] || fullState[indexName] || {};

    // Extract query and page separately:
    // - query goes to root index only (for the SearchBox widget)
    // - page is NOT passed to Algolia's uiState because we use an "infinite scroll" pattern
    //   where page is only used to calculate hitsPerPage in the Configure component.
    //   Including page in Algolia's state would cause it to skip hits on reload.
    const {
      page: _page, // eslint-disable-line @typescript-eslint/no-unused-vars
      query: activeTabQuery,
      ...refinementState
    } = activeTabState;

    // Restructure: root index gets only query, activeTab gets refinements/range/sortBy
    // (but NOT query since SearchBox is at root level, and NOT page since we use hitsPerPage accumulation)
    return {
      [indexName]: {
        query: activeTabQuery || '',
      },
      [activeTab]: refinementState,
    };
  }, [asPath, indexName, locale, activeTab, facetDataForTranslation]);

  // Check if URL has any active filters (refinements, range, or sortBy)
  // If so, we need to force-mount filter widgets so they can receive the state
  const hasUrlFilters = useMemo((): boolean => {
    const tabState = (uiStateFromURL[activeTab] || {}) as Record<
      string,
      unknown
    >;
    const refinementList = (tabState.refinementList || {}) as Record<
      string,
      string[]
    >;
    const range = (tabState.range || {}) as { price?: string };
    const sortBy = tabState.sortBy as string | undefined;

    // Check if there are any active refinements
    const hasRefinements = Object.values(refinementList).some(
      (values) => Array.isArray(values) && values.length > 0,
    );

    // Check if there's an active price range (not empty ":")
    const hasRange = Boolean(
      range.price && range.price !== ':' && range.price !== '0:0',
    );

    // Check if there's a non-default sort
    const hasSort = Boolean(sortBy && sortBy !== '');

    return hasRefinements || hasRange || hasSort;
  }, [uiStateFromURL, activeTab]);

  // Track when the filtered search results are ready to display
  // Always start hidden - SearchIndexStateApplier will signal when the search completes
  const [filteredResultsReady, setFilteredResultsReady] = useState(false);

  useEffect(() => {
    setFilteredResultsReady(false);
  }, [activeTab]);

  const pageNumber = parseInt(query.page as string, 10) || 1;
  const syncStateWithUrl = useCallback(
    (uiState: SearchUiState) => {
      // Pass facet data and locale to translate facet values to English for URL
      const queryParams = buildSearchPageQueryParamsFromUiState(
        uiState,
        facetDataForTranslation ?? undefined,
        currentLocaleCode,
      );
      const pathWithoutQuery = getPathnameFromAsPath(asPath);
      push({ pathname: pathWithoutQuery, query: queryParams }, undefined, {
        shallow: true,
      });
    },
    [push, query, facetDataForTranslation, currentLocaleCode],
  );

  useEffect(() => {
    let itemType = activeTab.toLowerCase();
    switch (itemType) {
      case 'watches':
        itemType = 'WATCH';
        break;
      case 'straps':
        itemType = 'STRAP';
        break;
      case 'eyewear':
        itemType = 'EYEWEAR';
        break;
      case 'stores':
        itemType = 'STORE';
        break;
      default:
        itemType = itemType.toUpperCase();
    }
    listingActions.updateListingType(itemType as ListingType);
  }, [activeTab]);

  const searchText =
    cmsDefaultLayoutData?.search?.title ?? formatMessage(commonMessages.search);

  const noResultContentData = cmsDefaultLayoutData?.search
    ?.noResultsContentCollection?.items as ContentfulData[];

  const strapFilters = applyProductConfigurationFilters(
    ProductConfigurationFilterType.SEARCH,
    'Strap',
    draftMode,
    '',
  );

  const [eyewearFilters, isEyewearSkuArray] = getEyewearFilters(
    query?.q,
    applyProductConfigurationFilters(
      ProductConfigurationFilterType.SEARCH,
      'Eyewear',
      draftMode,
    ),
  );
  const isEyewearSku =
    isEyewearSkuArray?.length > 0 ? isEyewearSkuArray[0] : false;

  const [watchFilters, isWatchSkuArray] = getWatchFilters(
    query?.q,
    applyProductConfigurationFilters(
      ProductConfigurationFilterType.SEARCH,
      'Watch',
      draftMode,
    ),
  );
  const isWatchSku = isWatchSkuArray?.length > 0 ? isWatchSkuArray[0] : false;

  // Don't render the search until userToken is ready
  if (isLoading) {
    return null;
  }

  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });

  // Destructure to remove initialUiState from instantSearchProps
  // We use the client-side computed uiStateFromURL instead
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initialUiState: _initialUiState,
    ...restInstantSearchProps
  } = (instantSearchProps as any) || {};

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        data-test-id='SearchViewInstantSearch'
        indexName={indexName}
        initialUiState={{
          // Only set the root index state with query - nested index state is applied
          // by SearchIndexStateApplier after widgets mount to avoid timing issues
          [indexName]: {
            query:
              (uiStateFromURL[indexName] as { query?: string })?.query || '',
          },
        }}
        insights={insightsOptions}
        searchClient={algoliaSearchClient}
        {...restInstantSearchProps}
        onStateChange={({ setUiState, uiState }) => {
          // Always update the internal InstantSearch state
          setUiState(uiState);

          // Skip URL sync until we've applied initial state from URL
          // The SearchIndexStateApplier will apply state after widgets mount
          if (isInitialMount.current) {
            return;
          }

          // Only sync to URL if the state has actually changed
          // This prevents redundant URL updates which can cause loops
          if (!isEqual(uiState, lastSyncedState.current)) {
            lastSyncedState.current = uiState;
            syncStateWithUrl(uiState);
          }
        }}
      >
        <Configure
          analyticsTags={[...SEARCH_PAGE_ROOT_ANALYTICS_TAGS]}
          distinct={!isWatchSku && !isEyewearSku}
          facetingAfterDistinct={true}
          filters={isWatchSku ? watchFilters : ''}
          hitsPerPage={HITS_PER_PAGE * pageNumber}
          queryType='prefixAll'
          typoTolerance={isWatchSku || isEyewearSku ? false : 'min'}
          userToken={userToken}
        />
        <ResetFilters />
        <AllTabResultsReady
          activeTab={activeTab}
          onReady={handleStateApplied}
        />

        <Container data-test-id='SearchViewContainer'>
          <S.Header data-test-id='SearchViewHeader' text={searchText} />

          <S.SearchInput
            data-test-id='SearchViewSearchInput'
            draftMode={draftMode}
            placeholder={cmsDefaultLayoutData?.search?.searchBox}
          />

          <ResultsTabsWrapper
            activeTab={activeTab}
            data-test-id='SearchViewResultsTabsWrapper'
            draftMode={draftMode}
            eyewearInitialNbHits={0}
            filteredResultsReady={filteredResultsReady}
            storeInitialNbHits={0}
            strapInitialNbHits={0}
            watchInitialNbHits={0}
          />

          <S.ResultsWrapper
            $isActive={activeTab === 'all'}
            data-test-id='SearchViewResultsWrapperAll'
          >
            {activeTab === 'all' && (
              <>
                <AllTabHitsSummary
                  filteredResultsReady={filteredResultsReady}
                />
                <ProductsResults
                  activeTab={activeTab}
                  data-test-id='SearchViewResultsAll'
                  hideNoResults={true}
                  hitType='watches'
                  initialResults={{ results: [] }}
                  preview={true}
                  useScopedResults={true}
                />
                <ProductsResults
                  activeTab={activeTab}
                  data-test-id='SearchViewResultsAll'
                  hideNoResults={true}
                  hitType='straps'
                  initialResults={{ results: [] }}
                  preview={true}
                  useScopedResults={true}
                />
                {isEyewearEnabled(draftMode) && (
                  <ProductsResults
                    activeTab={activeTab}
                    data-test-id='SearchViewResultsAllEyewear'
                    hideNoResults={true}
                    hitType='eyewear'
                    initialResults={{ results: [] }}
                    preview={true}
                    useScopedResults={true}
                  />
                )}
                <StoresResults
                  activeTab={activeTab}
                  data-test-id='SearchViewResultsAll'
                  hideNoResults={true}
                  hitType='stores'
                  initialResults={{ results: [] }}
                  preview={true}
                  useScopedResults={true}
                />
                <NoResultsAllTab
                  data-test-id='SearchViewNoResultsAllTab'
                  draftMode={draftMode}
                  initialCount={0}
                  noResultContentData={noResultContentData}
                />
              </>
            )}
          </S.ResultsWrapper>

          <S.ResultsWrapper
            $isActive={activeTab === 'watches'}
            data-test-id='SearchViewResultsWrapperWatches'
          >
            <Index
              indexId='watches'
              indexName={getIndexName('PRODUCT', locale)}
            >
              <PaginatedConfigure
                analyticsTags={getSearchScopeAnalyticsTags('watches')}
                distinct={false}
                filters={watchFilters}
                userToken={userToken}
              />
              {activeTab === 'watches' && (
                <>
                  <ListingFilters
                    collectionData={[]}
                    data-test-id='SearchViewListingFiltersWatches'
                    filteredResultsReady={filteredResultsReady}
                    forceMount={hasUrlFilters}
                    hideIfNoResults={true}
                    initialResults={{ results: [] }}
                    listingFiltersType='SEARCH_VIEW'
                    showCollectionFilter={true}
                  />
                  <SearchIndexStateApplier
                    expectedIndexState={uiStateFromURL['watches'] || {}}
                    indexId='watches'
                    onStateApplied={handleStateApplied}
                  />
                  <ProductsResults
                    activeTab={activeTab}
                    data-test-id='SearchViewResultsAll'
                    filteredResultsReady={filteredResultsReady}
                    hideNoResults
                    hitType='watches'
                    initialResults={{ results: [] }}
                    noResultContentData={noResultContentData}
                  />
                </>
              )}
            </Index>
          </S.ResultsWrapper>

          <S.ResultsWrapper
            $isActive={activeTab === 'straps'}
            data-test-id='SearchViewResultsWrapperStraps'
          >
            <Index indexId='straps' indexName={getIndexName('PRODUCT', locale)}>
              <PaginatedConfigure
                analyticsTags={getSearchScopeAnalyticsTags('straps')}
                distinct={false}
                filters={strapFilters}
                userToken={userToken}
              />
              {activeTab === 'straps' && (
                <>
                  <ListingFilters
                    collectionData={[]}
                    data-test-id='SearchViewListingFiltersStraps'
                    filteredResultsReady={filteredResultsReady}
                    forceMount={hasUrlFilters}
                    hideIfNoResults={true}
                    initialResults={{ results: [] }}
                    listingFiltersType='SEARCH_VIEW'
                  />
                  <SearchIndexStateApplier
                    expectedIndexState={uiStateFromURL['straps'] || {}}
                    indexId='straps'
                    onStateApplied={handleStateApplied}
                  />
                  <ProductsResults
                    activeTab={activeTab}
                    data-test-id='SearchViewResultsStraps'
                    filteredResultsReady={filteredResultsReady}
                    hitType='straps'
                    initialResults={{ results: [] }}
                    noResultContentData={noResultContentData}
                  />
                </>
              )}
            </Index>
          </S.ResultsWrapper>

          {isEyewearEnabled(draftMode) && (
            <S.ResultsWrapper
              $isActive={activeTab === 'eyewear'}
              data-test-id='SearchViewResultsWrapperEyewear'
            >
              <Index
                indexId='eyewear'
                indexName={getIndexName('PRODUCT', locale)}
              >
                <PaginatedConfigure
                  analyticsTags={getSearchScopeAnalyticsTags('sunglasses')}
                  distinct={false}
                  filters={eyewearFilters}
                  userToken={userToken}
                />
                {activeTab === 'eyewear' && (
                  <>
                    <ListingFilters
                      collectionData={[]}
                      data-test-id='SearchViewListingFiltersEyewear'
                      filteredResultsReady={filteredResultsReady}
                      forceMount={hasUrlFilters}
                      hideIfNoResults={true}
                      initialResults={{ results: [] }}
                      listingFiltersType='SEARCH_VIEW'
                    />
                    <SearchIndexStateApplier
                      expectedIndexState={uiStateFromURL['eyewear'] || {}}
                      indexId='eyewear'
                      onStateApplied={handleStateApplied}
                    />
                    <ProductsResults
                      activeTab={activeTab}
                      data-test-id='SearchViewResultsEyewear'
                      filteredResultsReady={filteredResultsReady}
                      hitType='eyewear'
                      initialResults={{ results: [] }}
                      noResultContentData={noResultContentData}
                    />
                  </>
                )}
              </Index>
            </S.ResultsWrapper>
          )}

          <S.ResultsWrapper
            $isActive={activeTab === 'stores'}
            data-test-id='SearchViewResultsWrapperStores'
          >
            <Index indexId='stores' indexName={getIndexName('STORE', locale)}>
              <PaginatedConfigure
                analyticsTags={getSearchScopeAnalyticsTags('stores')}
                distinct={true}
                filters='web_visible:true'
                userToken={userToken}
              />

              {activeTab === 'stores' && (
                <>
                  <ListingFilters
                    collectionData={[]}
                    data-test-id='SearchViewListingFiltersStores'
                    filteredResultsReady={filteredResultsReady}
                    forceMount={hasUrlFilters}
                    hideIfNoResults={true}
                    initialResults={{ results: [] }}
                    listingFiltersType='SEARCH_VIEW'
                  />
                  <SearchIndexStateApplier
                    expectedIndexState={uiStateFromURL['stores'] || {}}
                    indexId='stores'
                    onStateApplied={handleStateApplied}
                  />
                  <StoresResults
                    activeTab={activeTab}
                    data-test-id='SearchViewResultsStores'
                    filteredResultsReady={filteredResultsReady}
                    hitType='stores'
                    initialResults={{ results: [] }}
                    noResultContentData={noResultContentData}
                  />
                </>
              )}
            </Index>
          </S.ResultsWrapper>
        </Container>
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

SearchView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      cmsData={page.props.cmsDefaultLayoutData}
      data-test-id='SearchViewDefaultLayout'
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
